<template>
  <section class="dd-sub">
    <div v-if="!subscriptionsEnabled" class="dd-sub__notice">{{ $t('subscriptions.subscriptions_disabled') }}</div>
    <div v-else>
      <div v-if="!authenticated" class="dd-wall-card dd-login-post">
        <h3 class="dd-post__title">{{ $t('wall.home.login_tile_title') }}</h3>
        <p class="dd-post__text">{{ $t('wall.home.login_tile_text') }}</p>
        <div class="dd-post__cta dd-post__cta--right">
          <SigninWithGoogle />
        </div>
      </div>

      <GiftSubscriptionCard
        v-if="authenticated"
        class="dd-sub__gift-card"
        :title="$t('wall.subscription.gift_card_title')"
        :subtitle="$t('wall.subscription.gift_card_subtitle')"
        :note="$t('wall.subscription.gift_note_90_days')"
        :button-text="$t('wall.subscription.gift_button')"
        @action="openGiftDialog"
      >
        <template #actions>
          <SigninWithGoogle v-if="!authenticated" :href="giftCardLoginHref" />
          <button v-else class="btn-orange" type="button" @click="openGiftDialog">
            {{ $t('wall.subscription.gift_button') }}
          </button>
        </template>
      </GiftSubscriptionCard>

      <div v-if="giftPurchases.length" class="dd-wall-card dd-sub__gift-result">
        <div class="dd-sub__gift-result-header">
          <h3 class="dd-sub__gift-result-title">{{ $t('wall.subscription.gift_links_title') }}</h3>
          <button class="dd-sub__gift-toggle" type="button" @click="toggleGiftResultExpanded">
            {{ giftResultExpanded ? $t('wall.subscription.gift_hide') : $t('wall.subscription.gift_show') }}
          </button>
        </div>
        <p v-if="giftResultExpanded" class="dd-sub__gift-note">{{ $t('wall.subscription.gift_note_90_days') }}</p>
        <p v-else class="dd-sub__gift-note">{{ giftCollapsedSummary }}</p>
        <div v-if="giftResultExpanded" class="dd-sub__gift-links">
          <div v-for="purchase in giftPurchases" :key="purchase.id" class="dd-sub__gift-purchase">
            <div class="dd-sub__gift-purchase-header">
              <span class="dd-sub__gift-purchase-title">{{ giftPurchaseFooterText(purchase) }}</span>
              <span class="dd-sub__gift-status">{{ giftPurchaseStatusText(purchase) }}</span>
            </div>
            <p v-if="isGiftPendingStatus(purchase.status)" class="dd-sub__gift-hint">{{ $t('wall.subscription.gift_purchase_pending') }}</p>
            <p v-if="isGiftFailedStatus(purchase.status)" class="dd-sub__gift-hint dd-sub__gift-hint--error">{{ $t('wall.subscription.gift_purchase_failed') }}</p>
            <div v-if="purchase.links && purchase.links.length" class="dd-sub__gift-purchase-links">
              <div v-for="link in purchase.links" :key="link.id" class="dd-sub__gift-link">
                <input
                  class="dd-sub__gift-link-input"
                  :class="{ 'is-disabled': link.is_activated }"
                  :value="link.activation_url"
                  :disabled="link.is_activated"
                  readonly
                />
                <button
                  class="btn btn--outline dd-sub__gift-copy"
                  type="button"
                  :disabled="link.is_activated"
                  @click="copyGiftLink(link.id, link.activation_url)"
                >
                  {{ copiedLinkID === link.id ? $t('wall.subscription.gift_link_copied') : $t('wall.subscription.gift_copy_link') }}
                </button>
                <div class="dd-sub__gift-link-meta">
                  <span>{{ giftLinkStatusText(link) }}</span>
                  <span>{{ $t('wall.subscription.gift_link_expires_at', { date: formatDate(link.expires_at) }) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CurrentSubscriptionCard v-if="currentSubscription" @renew="openDialog(currentLevel || $event)" />

      <div class="dd-sub__levels">
        <h2 class="dd-sub__title">{{ $t('wall.subscription.select_level') }}</h2>
        <div class="dd-sub__grid">
          <LevelCard
            v-for="lvl in levels"
            :key="lvl.externalID"
            ref="sponsorshipLevelItems"
            :level="lvl"
            :currentLevel="currentLevel"
            :data-sponsorship-level-id="lvl.externalID"
            :class="{ 'sponsorship-level-deep-link-highlight': deepLinkedSponsorshipLevelID === lvl.externalID }"
            @subscribe="openDialog"
          />
        </div>
      </div>

      <div v-if="dialogOpen" class="dd-modal">
        <div class="dd-modal__backdrop" @click="closeDialog"></div>
        <div class="dd-modal__content">
          <div class="dd-modal__header">
            <h3>{{ $t('wall.subscription.confirm_title', { name: dialogLevel ? dialogLevel.name : '' }) }}</h3>
            <button class="dd-modal__close" @click="closeDialog" aria-label="Close">x</button>
          </div>
          <div class="dd-modal__body">
            <div class="dd-modal__field" v-if="paymentMethods.length > 1">
              <div v-if="isDowngrade" class="dd-modal__warning">
                {{ $t('wall.subscription.downgrade_crypto_hint', { name: dialogLevel ? dialogLevel.name : '' }) }}
              </div>
              <label class="dd-label dd-payment-method-label">{{ $t('wall.subscription.payment_method') }}</label>
              <ul class="dd-modal__options">
                <li v-for="m in paymentMethods" :key="m.id" :class="['dd-option', { 'is-active': m.id === selectedMethod }]">
                  <button type="button" class="dd-option__header" @click="selectedMethod = m.id">
                    <span class="dd-option__radio"><span class="dd-option__radio-dot"></span></span>
                    <div class="dd-option__copy">
                      <div class="dd-option__title">{{ paymentLabel(m.id) }}</div>
                    </div>
                  </button>
                </li>
              </ul>
            </div>
            <div class="dd-modal__field" v-else>
              <label class="dd-payment-method-label">{{ $t('wall.subscription.payment_method') }}</label>
              <div>{{ paymentLabel(firstPaymentId) }}</div>
            </div>

            <div v-if="!isDowngrade" class="dd-modal__field">
              <label class="dd-label">{{ $t('wall.subscription.period') }}</label>
              <div class="dd-period">
                <div class="dd-period__line"></div>
                <label v-for="p in periodOptions" :key="p" class="dd-period__item" :class="{ 'is-active': selectedPeriod === p }">
                  <input type="radio" :value="p" v-model.number="selectedPeriod" />
                  <span class="dd-period__discount" v-if="getDiscount(p) > 0">-{{ getDiscount(p) }}%</span>
                  <span class="dd-period__dot"><span class="dd-period__dot-inner"></span></span>
                  <span class="dd-period__label">{{ periodLabelShort(p) }}</span>
                </label>
              </div>
            </div>

            <div class="dd-modal__summary">
              <div class="dd-modal__summary-prices">
                <span class="dd-modal__summary-title">{{ $t('wall.subscription.total') }}</span>
                <div class="dd-modal__price">
                  <s v-if="originalPrice && discountValue > 0" class="dd-modal__price-old">{{ originalPrice }}</s>
                  <strong class="dd-modal__price-new">{{ totalPriceFormatted }}</strong>
                </div>
                <div v-if="creditValue > 0" class="dd-modal__credit">
                  - {{ $t('wall.subscription.credit') }}: {{ creditValue.toFixed(2) }} {{ dialogLevel.currency }}
                </div>
              </div>
              <div v-if="!isDowngrade && discountValue > 0" class="dd-modal__summary-discount">-{{ discountValue }}%</div>
            </div>
            <div
              v-if="creditValue > 0 || isFreeUpgrade"
              class="dd-modal__free-upgrade dd-modal__free-upgrade--wide"
            >
              <div v-if="creditValue > 0" class="dd-modal__free-upgrade-line">
                {{ $t('wall.subscription.credit_once_hint', { amount: netTotalPrice.toFixed(2), currency: dialogLevel ? dialogLevel.currency : '' }) }}
              </div>
              <div v-if="isFreeUpgrade" class="dd-modal__free-upgrade-line">
                {{ $t('wall.subscription.free_upgrade_without_payment') }}
                <br />
                <span>{{ freeUpgradePeriodLabel }}</span>
              </div>
            </div>
          </div>
          <div class="dd-modal__footer">
            <div class="dd-modal__hint">{{ cryptoHint }}</div>
            <div class="dd-modal__actions">
              <button class="btn btn--outline" @click="closeDialog">{{ $t('common.cancel') || 'Cancel' }}</button>
              <button class="btn" :disabled="busy" @click="confirm">{{ dialogCTA }}</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="giftDialogOpen" class="dd-modal">
        <div class="dd-modal__backdrop" @click="closeGiftDialog"></div>
        <div class="dd-modal__content">
          <div class="dd-modal__header">
            <h3>{{ $t('wall.subscription.gift_modal_title') }}</h3>
            <button class="dd-modal__close" @click="closeGiftDialog" aria-label="Close">x</button>
          </div>
          <div class="dd-modal__body">
            <div class="dd-modal__field">
              <label class="dd-label">{{ $t('wall.subscription.gift_level') }}</label>
              <ul class="dd-modal__options">
                <li
                  v-for="lvl in giftLevels"
                  :key="lvl.externalID"
                  :class="['dd-option', { 'is-active': giftDialogLevelID === lvl.ID }]"
                >
                  <button type="button" class="dd-option__header" @click="giftDialogLevelID = lvl.ID">
                    <span class="dd-option__radio"><span class="dd-option__radio-dot"></span></span>
                    <div class="dd-option__copy">
                      <div class="dd-option__title">{{ lvl.name }}</div>
                      <div class="dd-option__subtitle">{{ lvl.price.toFixed(2) }} {{ lvl.currency }} / {{ $t('wall.subscription.period_month') }}</div>
                    </div>
                  </button>
                </li>
              </ul>
            </div>

            <div class="dd-modal__field">
              <label class="dd-label dd-payment-method-label">{{ $t('wall.subscription.gift_payment_method') }}</label>
              <ul class="dd-modal__options">
                <li
                  v-for="m in giftPaymentMethods"
                  :key="m.id"
                  :class="['dd-option', { 'is-active': giftSelectedMethod === m.id }]"
                >
                  <button type="button" class="dd-option__header" @click="giftSelectedMethod = m.id">
                    <span class="dd-option__radio"><span class="dd-option__radio-dot"></span></span>
                    <div class="dd-option__copy">
                      <div class="dd-option__title">{{ paymentLabel(m.id) }}</div>
                    </div>
                  </button>
                </li>
              </ul>
            </div>

            <div class="dd-modal__field">
              <label class="dd-label">{{ $t('wall.subscription.gift_period') }}</label>
              <div class="dd-period">
                <div class="dd-period__line"></div>
                <label v-for="p in giftPeriodOptions" :key="p" class="dd-period__item" :class="{ 'is-active': giftSelectedPeriod === p }">
                  <input type="radio" :value="p" v-model.number="giftSelectedPeriod" />
                  <span class="dd-period__discount" v-if="getGiftDiscount(p) > 0">-{{ getGiftDiscount(p) }}%</span>
                  <span class="dd-period__dot"><span class="dd-period__dot-inner"></span></span>
                  <span class="dd-period__label">{{ periodLabelShort(p) }}</span>
                </label>
              </div>
            </div>

            <div class="dd-modal__field">
              <label class="dd-label" for="gift-quantity">{{ $t('wall.subscription.gift_quantity') }}</label>
              <input
                id="gift-quantity"
                v-model.number="giftQuantity"
                class="dd-modal__input"
                type="number"
                min="1"
                max="100"
                step="1"
                @change="clampGiftQuantity"
              />
            </div>

            <div class="dd-modal__summary">
              <div class="dd-modal__summary-prices">
                <span class="dd-modal__summary-title">{{ $t('wall.subscription.total') }}</span>
                <div class="dd-modal__price">
                  <strong class="dd-modal__price-new">{{ giftTotalFormatted }}</strong>
                </div>
              </div>
            </div>
            <div class="dd-modal__hint">{{ giftCryptoHint }}</div>
            <div class="dd-modal__hint">{{ $t('wall.subscription.gift_note_90_days') }}</div>
          </div>
          <div class="dd-modal__footer">
            <div></div>
            <div class="dd-modal__actions">
              <button class="btn btn--outline" @click="closeGiftDialog">{{ $t('common.cancel') || 'Cancel' }}</button>
              <button class="btn" :disabled="busy || !giftDialogLevel" @click="confirmGift">{{ $t('wall.subscription.gift_buy_button') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>


<script>
import CurrentSubscriptionCard from '@/new/components/wall/CurrentSubscriptionCard.vue';
import LevelCard from '@/new/components/wall/LevelCard.vue';
import SigninWithGoogle from '@/new/components/general/SigninWithGoogle.vue';
import GiftSubscriptionCard from '@/new/components/wall/GiftSubscriptionCard.vue';
import { mapGetters } from 'vuex';
import SubscriptionPaymentMethodStatus from '@/store/models/SubscriptionPaymentMethodStatus';
import { logWarn } from '@/helpers';
import sponsorshipLevelDeepLink from '@/sponsorshipLevelDeepLink';

export default {
  name: 'WallSubscription',
  mixins: [sponsorshipLevelDeepLink],
  components: { CurrentSubscriptionCard, LevelCard, SigninWithGoogle, GiftSubscriptionCard },
  data() {
    return {
      dialogOpen: false,
      dialogLevel: null,
      selectedPeriod: 1,
      selectedMethod: null,
      giftDialogOpen: false,
      giftDialogLevelID: null,
      giftSelectedPeriod: 1,
      giftSelectedMethod: 'card',
      giftQuantity: 1,
      copiedLinkID: null,
      copiedLinkTimeout: null,
      giftPollTimer: null,
      giftPollAttempts: 0,
      giftResultExpanded: false,
      giftResultToggleTouched: false,
    };
  },
  computed: {
    ...mapGetters([
      'allowedCryptoSymbols',
      'discountedPrice',
      'availablePeriodsForLevel',
      'subscriptionDiscountsEnabled',
      'subscriptionPaymentIntent',
      'cryptocurrencyPaymentPurpose',
      'currentGiftPurchase',
      'currentGiftPurchases',
    ]),
    currentSubscription() { return this.$store.getters.currentSubscription; },
    currentLevel() { return this.$store.getters.currentLevel; },
    levels() { return this.$store.getters.sponsorshipLevels; },
    subscriptionsEnabled() { return this.$store.getters.subscriptionsEnabled; },
    authenticated() { return this.$store.getters.authenticated; },
    busy() { return this.$store.getters.subscriptionRequestInProgress; },
    giftPurchases() {
      if (Array.isArray(this.currentGiftPurchases) && this.currentGiftPurchases.length) {
        return this.currentGiftPurchases;
      }
      return this.currentGiftPurchase ? [this.currentGiftPurchase] : [];
    },
    giftTotalPurchased() {
      return this.giftPurchases.reduce((total, purchase) => {
        const quantity = Number(purchase?.quantity || 0);
        return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
      }, 0);
    },
    giftTotalNotActivated() {
      return this.giftPurchases.reduce((total, purchase) => total + this.giftNotActivatedCount(purchase), 0);
    },
    giftCollapsedSummary() {
      return this.$t('wall.subscription.gift_collapsed_summary', {
        purchased: this.giftTotalPurchased,
        notActivated: this.giftTotalNotActivated,
      });
    },
    giftLevels() {
      return (this.levels || []).filter((lvl) => lvl && lvl.isAvailable);
    },
    giftDialogLevel() {
      return this.giftLevels.find((lvl) => lvl.ID === this.giftDialogLevelID) || null;
    },
    giftCardLoginHref() {
      return `/login?provider=google&redirect=${encodeURIComponent('/wall/subscription?open_gift=1')}`;
    },
    firstPaymentId() {
      return (this.paymentMethods[0] && this.paymentMethods[0].id) || '';
    },
    discountValue() { return this.isDowngrade ? 0 : this.getDiscount(this.selectedPeriod); },
    totalPrice() { return this.discountedPrice(this.dialogLevel, this.selectedPeriod); },
    creditValue() {
      if (!this.isUpgrade) return 0;
      const credit = this.$store.getters.subscriptionCredit || 0;
      return credit > 0 ? credit : 0;
    },
    netTotalPrice() {
      const raw = this.totalPrice - this.creditValue;
      return raw > 0 ? raw : 0;
    },
    totalPriceFormatted() {
      if (!this.dialogLevel) return '';
      return `${this.netTotalPrice.toFixed(2)} ${this.dialogLevel.currency}`;
    },
    originalPrice() {
      if (!this.dialogLevel) return '';
      const val = (this.dialogLevel.price || 0) * (this.selectedPeriod || 1);
      return `${val.toFixed(2)} ${this.dialogLevel.currency}`;
    },
    cryptoHint() {
      if (!this.dialogLevel) return '';
      if (!this.selectedMethod || !this.selectedMethod.startsWith('crypto:')) return '';
      const symbol = this.selectedMethod.split(':')[1];
      const rate = this.$store.state.rateMultipliers?.[symbol];
      if (!rate) return '';
      const amount = (this.netTotalPrice || 0) * rate;
      if (!amount) return '';
      return `~= ${amount.toFixed(6)} ${symbol}`;
    },
    giftPaymentMethods() {
      const methods = [];
      const allowedMethods = this.$store.state.allowedPaymentMethods || [];
      if (allowedMethods.includes('donation')) {
        methods.push({ id: 'card', label: this.$t('wall.subscription.payment_method_card') });
      }
      this.subscriptionCryptoSymbols.forEach((sym) => {
        methods.push({ id: `crypto:${sym}`, label: this.$t(`forms.payment_mode_${sym}`) || `${sym}` });
      });
      if (!methods.length) {
        methods.push({ id: 'card', label: this.$t('wall.subscription.payment_method_card') });
      }
      return methods;
    },
    giftPeriodOptions() {
      if (!this.giftDialogLevel) return [1];
      const periods = this.availablePeriodsForLevel(this.giftDialogLevel) || [];
      return periods.length ? periods : [1];
    },
    giftTotalPrice() {
      if (!this.giftDialogLevel) return 0;
      const unit = this.discountedPrice(this.giftDialogLevel, this.giftSelectedPeriod);
      const qty = Number(this.giftQuantity) || 1;
      return Math.max(0, unit * qty);
    },
    giftTotalFormatted() {
      if (!this.giftDialogLevel) return '';
      return `${this.giftTotalPrice.toFixed(2)} ${this.giftDialogLevel.currency}`;
    },
    giftCryptoHint() {
      if (!this.giftDialogLevel) return '';
      if (!this.giftSelectedMethod || !this.giftSelectedMethod.startsWith('crypto:')) return '';
      const symbol = this.giftSelectedMethod.split(':')[1];
      const rate = this.$store.state.rateMultipliers?.[symbol];
      if (!rate) return '';
      const amount = this.giftTotalPrice * rate;
      if (!amount) return '';
      return `~= ${amount.toFixed(6)} ${symbol}`;
    },
    paymentMethods() {
      const methods = [];
      const allowedMethods = this.$store.state.allowedPaymentMethods || [];
      if (allowedMethods.includes('donation')) {
        methods.push({ id: 'card', label: this.$t('forms.payment_mode_subscription') });
      }
      if (!this.isDowngrade) {
        this.subscriptionCryptoSymbols.forEach(sym => {
          methods.push({ id: `crypto:${sym}`, label: this.$t(`forms.payment_mode_${sym}`) || `${sym}` });
        });
      }
      if (!methods.length) {
        methods.push({ id: 'card', label: this.$t('forms.payment_mode_subscription') });
      }
      return methods;
    },
    subscriptionCryptoSymbols() {
      const allowedMethods = this.$store.state.allowedPaymentMethods || [];
      return this.allowedCryptoSymbols.filter(sym => allowedMethods.includes(sym));
    },
    periodOptions() {
      if (this.isDowngrade) return [1];
      return this.availablePeriodsForLevel(this.dialogLevel);
    },
    dialogCTA() {
      return this.currentLevel && this.dialogLevel && this.currentLevel.externalID === this.dialogLevel.externalID
        ? this.$t('wall.subscription.pay')
        : this.$t('wall.subscription.subscribe');
    },
    isDowngrade() {
      return !!(this.currentLevel && this.dialogLevel && this.currentLevel.pricePerYearInUSD > this.dialogLevel.pricePerYearInUSD);
    },
    isUpgrade() {
      return !!(this.currentLevel && this.dialogLevel && this.currentLevel.pricePerYearInUSD < this.dialogLevel.pricePerYearInUSD);
    },
    isFreeUpgrade() {
      // Free upgrade when credit fully covers the price (or more).
      return this.isUpgrade && this.netTotalPrice <= 0;
    },
    freeUpgradePeriodLabel() {
      if (!this.isFreeUpgrade || !this.dialogLevel || !this.selectedPeriod) return '';
      const totalPrice = this.totalPrice;
      const credit = this.creditValue;
      if (!(totalPrice > 0 && credit > 0)) return '';
      const factor = credit / totalPrice;
      if (!isFinite(factor) || factor <= 0) return '';
      const months = this.selectedPeriod;
      const now = new Date();
      const endBase = new Date(now.getTime());
      endBase.setMonth(endBase.getMonth() + months);
      const periodMs = endBase.getTime() - now.getTime();
      if (periodMs <= 0) return '';
      const extendedMs = periodMs * factor;
      const totalDays = Math.round(extendedMs / (24 * 60 * 60 * 1000));
      const m = Math.floor(totalDays / 30);
      const d = totalDays - m * 30;
      return this.$t('wall.subscription.free_upgrade_period', { months: m, days: d });
    },
  },
  methods: {
    openDialog(level, { method = null, period = null } = {}) {
      // TODO: remove this logic after downgrade with discount implementation
      const current = this.currentLevel;
      const isDowngrade = !!(current && level && current.pricePerYearInUSD > level.pricePerYearInUSD);
      const paymentStatus = this.$store.getters.subscriptionPaymentMethodStatus;
      const isStripe = paymentStatus && paymentStatus !== SubscriptionPaymentMethodStatus.NonRecurrent;
      if (isDowngrade && isStripe) {
        this.$store.dispatch('subscribe', level.ID);
        return;
      }

      this.dialogLevel = level;
      this.dialogOpen = true;
      const fallbackMethod = this.paymentMethods[0]?.id || 'card';
      this.selectedMethod = method && this.paymentMethods.some((option) => option.id === method) ? method : fallbackMethod;
      const discounted = this.periodOptions.find(p => this.getDiscount(p) > 0) || 1;
      const chosenPeriod = period && this.periodOptions.includes(period) ? period : discounted;
      this.selectedPeriod = chosenPeriod;
      if (this.isDowngrade) {
        this.selectedMethod = 'card';
        this.selectedPeriod = 1;
      }
    },
    maybeOpenFromIntent() {
      const intent = this.subscriptionPaymentIntent;
      if (!intent) return;
      const level = this.levels.find(l => l.ID === intent.levelId);
      if (!level) return;
      const isCrypto = this.cryptocurrencyPaymentPurpose === 'subscription' && intent.paymentCurrency;
      const methodId = isCrypto ? `crypto:${intent.paymentCurrency || 'BTC'}` : 'card';
      this.openDialog(level, { method: methodId, period: intent.months });
    },
    maybeOpenGiftFromQuery() {
      if (!this.authenticated) return;
      const requested = this.$route.query.open_gift;
      if (!requested) return;
      if (requested === '1' || requested === 'true') {
        this.openGiftDialog();
      }
    },
    maybeLoadGiftPurchaseFromQuery() {
      if (!this.authenticated) return;
      const purchaseID = Number(this.$route.query.gift_purchase_id || 0);
      this.syncGiftResultExpandedDefault();
      if (purchaseID && Number.isFinite(purchaseID)) {
        this.$store.dispatch('fetchGiftSubscriptionPurchase', purchaseID).then((purchase) => {
          if (purchase && this.isGiftPendingStatus(purchase.status)) {
            this.startGiftPolling(purchaseID);
          } else {
            this.stopGiftPolling();
          }
        });
        return;
      }
      this.$store.dispatch('fetchLatestGiftSubscriptionPurchase').then((purchase) => {
        if (!purchase) {
          this.stopGiftPolling();
          return;
        }
        if (this.isGiftPendingStatus(purchase.status)) {
          this.startGiftPolling(purchase.id);
        } else {
          this.stopGiftPolling();
        }
      });
    },
    isGiftPendingStatus(status) {
      return status === 'initiated' || status === 'awaiting_blockchain';
    },
    startGiftPolling(purchaseID) {
      this.stopGiftPolling();
      this.giftPollAttempts = 0;
      this.giftPollTimer = setInterval(() => {
        this.giftPollAttempts += 1;
        if (this.giftPollAttempts > 60) {
          this.stopGiftPolling();
          return;
        }
        this.$store.dispatch('fetchGiftSubscriptionPurchase', purchaseID).then((purchase) => {
          if (!purchase || !this.isGiftPendingStatus(purchase.status)) {
            this.stopGiftPolling();
          }
        });
      }, 4000);
    },
    stopGiftPolling() {
      if (this.giftPollTimer) {
        clearInterval(this.giftPollTimer);
        this.giftPollTimer = null;
      }
    },
    syncGiftResultExpandedDefault() {
      const hasRecentPurchase = Number(this.$route.query.gift_purchase_id || 0) > 0;
      if (hasRecentPurchase) {
        this.giftResultExpanded = true;
        return;
      }
      if (!this.giftResultToggleTouched) {
        this.giftResultExpanded = false;
      }
    },
    toggleGiftResultExpanded() {
      this.giftResultExpanded = !this.giftResultExpanded;
      this.giftResultToggleTouched = true;
    },
    openGiftDialog() {
      if (!this.authenticated) {
        return;
      }
      const level = this.giftDialogLevel || this.currentLevel || this.giftLevels[0] || null;
      this.giftDialogLevelID = level ? level.ID : null;
      this.giftSelectedPeriod = this.giftPeriodOptions.includes(1) ? 1 : (this.giftPeriodOptions[0] || 1);
      this.giftSelectedMethod = this.giftPaymentMethods[0]?.id || 'card';
      this.giftQuantity = 1;
      this.giftDialogOpen = true;
    },
    closeGiftDialog() {
      this.giftDialogOpen = false;
    },
    clampGiftQuantity() {
      let qty = Number(this.giftQuantity);
      if (!Number.isFinite(qty)) qty = 1;
      qty = Math.round(qty);
      if (qty < 1) qty = 1;
      if (qty > 100) qty = 100;
      this.giftQuantity = qty;
    },
    async confirmGift() {
      if (!this.giftDialogLevel) return;
      this.clampGiftQuantity();
      const isCrypto = this.giftSelectedMethod && this.giftSelectedMethod.startsWith('crypto:');
      const paymentCurrency = isCrypto ? this.giftSelectedMethod.split(':')[1] : null;

      try {
        const response = await this.$store.dispatch('createGiftSubscriptionPurchase', {
          levelId: this.giftDialogLevel.ID,
          months: this.giftSelectedPeriod,
          quantity: this.giftQuantity,
          method: isCrypto ? 'crypto' : 'card',
          paymentCurrency,
        });
        const isCryptoFlow = !!response?.address_uuid;
        const purchaseID = response?.purchase_id || response?.purchase?.id || null;
        if (purchaseID && !isCryptoFlow) {
          const nextQuery = { ...this.$route.query, gift_purchase_id: `${purchaseID}` };
          delete nextQuery.open_gift;
          this.$router.replace({ query: nextQuery }).catch((err) => {
            if (!err || err.name !== 'NavigationDuplicated') {
              logWarn('Failed to update gift purchase query', err);
            }
          });
          this.$store.dispatch('fetchGiftSubscriptionPurchase', purchaseID);
        }
      } finally {
        this.closeGiftDialog();
      }
    },
    closeDialog() {
      this.dialogOpen = false;
      this.dialogLevel = null;
      this.selectedPeriod = 1;
    },
    periodLabelShort(p) {
      if (p === 1) return this.$t('wall.subscription.period_month');
      return this.$t('wall.subscription.period_months', { months: p });
    },
    giftNotActivatedCount(purchase) {
      if (!purchase) return 0;
      if (Array.isArray(purchase.links) && purchase.links.length > 0) {
        return purchase.links.reduce((total, link) => total + (link?.is_activated ? 0 : 1), 0);
      }
      const quantity = Number(purchase.quantity || 0);
      const activatedCount = Number(purchase.activated_count || purchase.activatedCount || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) return 0;
      if (!Number.isFinite(activatedCount) || activatedCount <= 0) return quantity;
      return Math.max(0, quantity - activatedCount);
    },
    isGiftFailedStatus(status) {
      return status === 'failed' || status === 'expired' || status === 'refunded';
    },
    giftPurchaseStatusText(purchase) {
      const status = purchase?.status;
      if (!status) return '';
      const key = `wall.subscription.gift_purchase_status_${status}`;
      return this.$i18n.te(key) ? this.$t(key) : status;
    },
    giftPurchaseFooterText(purchase) {
      if (!purchase) return '';
      const fallbackLevel = this.levels.find((lvl) => lvl.ID === purchase.level_id || lvl.ID === purchase.levelID);
      const levelName = purchase.level_name || purchase.levelName || fallbackLevel?.name || '';
      const months = Number(purchase.months || 1);
      const quantity = Number(purchase.quantity || 1);
      let amount = Number(purchase.amount_fiat || purchase.amountFiat || 0);
      if (quantity > 1 && amount > 0) {
        amount = amount / quantity;
      }
      const currency = purchase.currency_fiat || purchase.currencyFiat || fallbackLevel?.currency || '';
      const periodText = this.periodLabelShort(months);
      const priceText = amount > 0 ? `${amount.toFixed(2)} ${currency}` : '';
      const parts = [levelName, periodText, priceText].filter(Boolean);
      return parts.join(' • ');
    },
    paymentLabel(id) {
      if (!id) return '';
      if (id === 'card') return this.$t('wall.subscription.payment_method_card');
      if (id.startsWith('crypto:')) {
        const sym = id.split(':')[1];
        const lower = (sym || '').toLowerCase();
        if (lower === 'btc') return this.$t('wall.subscription.payment_method_btc');
        if (lower === 'xmr') return this.$t('wall.subscription.payment_method_xmr');
        return sym ? `${sym}` : '';
      }
      return id;
    },
    getDiscount(months) {
      if (!this.dialogLevel || !this.subscriptionDiscountsEnabled) return 0;
      if (months === 3) return this.dialogLevel.discount3m || 0;
      if (months === 6) return this.dialogLevel.discount6m || 0;
      if (months === 12) return this.dialogLevel.discount12m || 0;
      return 0;
    },
    getGiftDiscount(months) {
      if (!this.giftDialogLevel || !this.subscriptionDiscountsEnabled) return 0;
      if (months === 3) return this.giftDialogLevel.discount3m || 0;
      if (months === 6) return this.giftDialogLevel.discount6m || 0;
      if (months === 12) return this.giftDialogLevel.discount12m || 0;
      return 0;
    },
    async confirm() {
      if (!this.dialogLevel) return;
      const isCrypto = this.selectedMethod && this.selectedMethod.startsWith('crypto:');
      const currency = isCrypto ? this.selectedMethod.split(':')[1] : null;
      const isCurrent = this.currentLevel && this.currentLevel.externalID === this.dialogLevel.externalID;
      try {
        if (!isCrypto && isCurrent) {
          this.$store.commit('setSubscriptionPaymentIntent', null);
          await this.$store.dispatch('paySubscription', this.selectedPeriod);
        } else {
          if (!isCrypto) {
            this.$store.commit('setSubscriptionPaymentIntent', null);
          }
          await this.$store.dispatch('subscribeWithOptions', {
            levelId: this.dialogLevel.ID,
            months: this.selectedPeriod,
            method: isCrypto ? 'crypto' : 'card',
            paymentCurrency: currency,
          });
        }
      } finally {
        this.closeDialog();
      }
    },
    async copyGiftLink(linkID, activationURL) {
      if (!activationURL) return;
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(activationURL);
        } else {
          const input = document.createElement('input');
          input.value = activationURL;
          document.body.appendChild(input);
          input.focus();
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        this.copiedLinkID = linkID;
        if (this.copiedLinkTimeout) {
          clearTimeout(this.copiedLinkTimeout);
        }
        this.copiedLinkTimeout = setTimeout(() => {
          this.copiedLinkID = null;
          this.copiedLinkTimeout = null;
        }, 1800);
      } catch (error) {
        logWarn('Failed to copy gift activation link', error);
      }
    },
    giftLinkStatusText(link) {
      if (!link) return '';
      if (link.is_activated) {
        return this.$t('wall.subscription.gift_link_status_activated');
      }
      const expiresAt = new Date(link.expires_at);
      if (expiresAt < new Date()) {
        return this.$t('wall.subscription.gift_link_status_expired');
      }
      return this.$t('wall.subscription.gift_link_status_not_activated');
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString();
    },
  },
  mounted() {
    this.maybeOpenFromIntent();
    this.maybeOpenGiftFromQuery();
    this.maybeLoadGiftPurchaseFromQuery();
  },
  beforeDestroy() {
    this.stopGiftPolling();
    if (this.copiedLinkTimeout) {
      clearTimeout(this.copiedLinkTimeout);
      this.copiedLinkTimeout = null;
    }
  },
  watch: {
    levels() {
      this.maybeOpenFromIntent();
      if (!this.giftDialogLevelID && this.giftLevels.length > 0) {
        this.giftDialogLevelID = this.giftLevels[0].ID;
      }
    },
    subscriptionPaymentIntent() {
      this.maybeOpenFromIntent();
    },
    giftDialogLevelID() {
      if (!this.giftPeriodOptions.includes(this.giftSelectedPeriod)) {
        this.giftSelectedPeriod = this.giftPeriodOptions[0] || 1;
      }
    },
    authenticated() {
      this.maybeOpenGiftFromQuery();
      this.maybeLoadGiftPurchaseFromQuery();
    },
    '$route.query.open_gift'() {
      this.maybeOpenGiftFromQuery();
    },
    '$route.query.gift_purchase_id'() {
      this.maybeLoadGiftPurchaseFromQuery();
    },
  },
};
</script>


<style scoped>
.dd-sub__gift-card {
  margin-bottom: 16px;
}
.dd-sub__gift-result {
  margin-top: 18px;
  margin-bottom: 16px;
}
.dd-sub__gift-result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.dd-sub__gift-result-title {
  margin: 0;
}
.dd-sub__gift-toggle {
  border: none;
  background: transparent;
  color: var(--dd-accent);
  font-size: 13px;
  font-weight: 500;
  padding: 0;
  cursor: pointer;
}
.dd-sub__gift-toggle:hover {
  text-decoration: underline;
}
.dd-sub__gift-status {
  font-size: 13px;
  color: #555;
}
.dd-sub__gift-note {
  margin: 10px 0 0;
  color: #666;
}
.dd-sub__gift-hint {
  margin: 10px 0 0;
  color: #666;
}
.dd-sub__gift-hint--error {
  color: #9a2f2f;
}
.dd-sub__gift-links {
  margin-top: 12px;
  display: grid;
  gap: 10px;
}
.dd-sub__gift-purchase {
  border: 1px solid var(--dd-border);
  border-radius: 10px;
  padding: 10px;
  display: grid;
  gap: 8px;
}
.dd-sub__gift-purchase-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.dd-sub__gift-purchase-title {
  font-size: 13px;
  color: #666;
}
.dd-sub__gift-purchase-links {
  display: grid;
  gap: 10px;
}
.dd-sub__gift-link {
  border: 1px solid var(--dd-border);
  border-radius: 10px;
  padding: 10px;
  display: grid;
  gap: 8px;
}
.dd-sub__gift-link-input {
  width: 100%;
  border: 1px solid var(--dd-border);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #222;
}
.dd-sub__gift-link-input.is-disabled {
  background: #f3f3f3;
  color: #777;
  cursor: not-allowed;
}
.dd-sub__gift-copy {
  justify-self: start;
}
.dd-sub__gift-copy:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.dd-sub__gift-link-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #666;
  font-size: 13px;
}

.dd-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.dd-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
}
.dd-modal__content {
  position: relative;
  background: #fff;
  color: #000;
  padding: 20px;
  border-radius: 12px;
  width: 90%;
  max-width: 560px;
  max-height: calc(100vh - 24px);
  overflow: auto;
  z-index: 1;
}
.dd-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.dd-modal__close {
  border: none;
  background: transparent;
  font-size: 20px;
  cursor: pointer;
}
.dd-modal__body {
  margin-top: 12px;
  display: grid;
  gap: 12px;
}
.dd-modal__options {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.dd-option {
  border: 1px solid var(--dd-border);
  border-radius: 12px;
  background: var(--dd-card-bg, #fff);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.dd-option.is-active {
  border-color: var(--dd-accent);
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
}
.dd-option__header {
  border: none;
  background: transparent;
  width: 100%;
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  text-align: left;
}
.dd-option__radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--dd-border);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dd-option__radio-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
}
.dd-option.is-active .dd-option__radio { border-color: var(--dd-accent); }
.dd-option.is-active .dd-option__radio-dot { background: var(--dd-accent); }
.dd-option__title { font-weight: 600; }
.dd-option__subtitle {
  margin-top: 2px;
  color: #666;
  font-size: 13px;
}

.dd-period {
  --dot-size: 20px;
  --pad-x: 20px;
  --pad-top: 18px;
  position: relative;
  padding: var(--pad-top) var(--pad-x) 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0;
  flex-wrap: nowrap;
}
.dd-period__line {
  position: absolute;
  top: calc(var(--pad-top) + var(--dot-size) / 2 - 1px);
  left: var(--pad-x);
  right: var(--pad-x);
  height: 2px;
  background: var(--dd-border);
  z-index: 0;
}
.dd-period__item {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 0;
  flex: 1;
  text-align: center;
}
.dd-period__item input { display: none; }
.dd-period__dot {
  width: var(--dot-size);
  height: var(--dot-size);
  border-radius: 50%;
  border: 2px solid var(--dd-border);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  position: relative;
  top: 0;
}
.dd-period__dot-inner {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
}
.dd-period__item.is-active .dd-period__dot { border-color: var(--dd-accent); }
.dd-period__item.is-active .dd-period__dot-inner { background: var(--dd-accent); }
.dd-period__discount {
  color: var(--dd-accent);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  position: absolute;
  top: -14px;
  left: calc(50% + 2px);
  transform: translateX(-50%);
}
.dd-period__label {
  font-size: 13px;
  color: #555;
}
.dd-modal__summary {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}
.dd-modal__summary-title { display: block; margin-bottom: 4px; }
.dd-modal__price { display: flex; flex-direction: column; gap: 4px; }
.dd-modal__price-old { color: #888; }
.dd-modal__price-new { font-size: 18px; }
.dd-modal__credit { font-size: 13px; color: #555; }
.dd-modal__summary-discount {
  color: var(--dd-accent);
  font-weight: 700;
  margin-top: 12px;
}
.dd-modal__footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  align-items: center;
}
.dd-modal__actions { display: flex; gap: 8px; }
.dd-modal__hint { color: #666; font-size: 13px; }
.dd-payment-method-label {
  display: block;
  padding-bottom: 10px !important;
}
.dd-modal__warning {
  background: #fff7e6;
  border: 1px solid #ffd8a8;
  color: #8a5a00;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 10px;
  font-size: 13px;
}
.dd-modal__free-upgrade {
  font-weight: 400;
  background: #fff7e6;
  border: 1px solid #ffd8a8;
  color: #8a5a00;
  padding: 10px;
  border-radius: 8px;
  font-size: 14px;
}
.dd-modal__free-upgrade--wide {
  margin-top: 8px;
}
.dd-modal__free-upgrade-line + .dd-modal__free-upgrade-line {
  margin-top: 6px;
}
.dd-modal__input {
  width: 100%;
  border: 1px solid var(--dd-border);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
}
</style>
